import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import VideoPlayer from './VideoPlayer';
import KeyMetrics from './KeyMetrics';
// import AnalysisPeriod from './AnalysisPeriod';
import DetectionChart from './DetectionChart';
import { fetchUrl } from '@utils/api';
import { LuFileDown, LuLoader } from 'react-icons/lu';
import { TbVideo } from 'react-icons/tb';
import { getCountingMethodConfig } from './countingMethodConfig';

const Main = styled.div`
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	background: ${({ theme }) => theme.colors.background};
	min-height: 0;
`;

const MainHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 10px 20px;
	border-bottom: 1px solid ${({ theme }) => theme.colors.border};
	flex-shrink: 0;
	min-height: 44px;
`;

const HeaderTitle = styled.h2`
	font-size: 15px;
	font-weight: 600;
	margin: 0;
`;

const HeaderActions = styled.div`
	display: flex;
	gap: 6px;
`;

const ActionButton = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	padding: 5px 12px;
	border-radius: 5px;
	font-family: inherit;
	font-size: 11px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s;
	border: 1px solid ${({ theme }) => theme.colors.border};
	background: ${({ theme }) => theme.colors.backgroundAccent};
	color: ${({ theme }) => theme.colors.textColor};

	&:hover {
		border-color: ${({ theme }) => theme.colors.green};
		color: ${({ theme }) => theme.colors.green};
	}
`;

const ResultsBody = styled.div`
	flex: 1;
	display: grid;
	grid-template-rows: 1fr auto auto;
	padding: 12px 16px;
	gap: 10px;
	min-height: 0;
`;

const VideoArea = styled.div`
	display: grid;
	grid-template-columns: 1fr 200px;
	gap: 12px;
	min-height: 0;
	overflow: hidden;
`;

/* ===== Empty / Pending states (inline) ===== */
const StateContainer = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 12px;
	color: ${({ theme }) => theme.colors.textAccent};
`;

const spin = keyframes`
	from { transform: rotate(0deg); }
	to { transform: rotate(360deg); }
`;

const StateIcon = styled.div`
	opacity: 0.3;
	display: flex;
	align-items: center;
	justify-content: center;
	color: ${({ theme }) => theme.colors.textAccent};

	&.spinning svg {
		animation: ${spin} 1.5s linear infinite;
	}
`;

const StateText = styled.p`
	font-size: 14px;
	margin: 0;
`;

const pulse = keyframes`
	0% { opacity: 1; }
	50% { opacity: 0.5; }
	100% { opacity: 1; }
`;

const ProgressTrack = styled.div`
	width: 200px;
	height: 4px;
	background: ${({ theme }) => theme.colors.backgroundAccent};
	border-radius: 2px;
	overflow: hidden;
`;

const ProgressFill = styled.div`
	width: ${({ $pct }) => $pct}%;
	height: 100%;
	background: ${({ theme }) => theme.colors.green};
	border-radius: 2px;
	animation: ${pulse} 2s infinite;
`;

const ProgressRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const ProgressLabel = styled.span`
	font-size: 11px;
	color: ${({ theme }) => theme.colors.textAccent};
`;

const formatTimestamp = (seconds, fps) => {
	if (seconds == null || isNaN(seconds)) return '—';
	const totalFrames = fps ? Math.round(seconds * fps) : null;
	const m = Math.floor(seconds / 60);
	const s = (seconds % 60).toFixed(3);
	const time = `${String(m).padStart(2, '0')}:${s.padStart(6, '0')}`;
	// return totalFrames != null ? `${time} (frame ${totalFrames})` : time;
	// TODO: fix this
	return time;
};

const ResultsPanel = ({
	jobStatus,
	jobProgress,
	videoUrl,
	detections,
	results,
	analysis,
	onExport,
	onReprocess,
	onStartDetection,
	onSetPeriod,
	onExportCsv,
}) => {
	const [signedVideoUrl, setSignedVideoUrl] = useState(null);
	const [chartData, setChartData] = useState(null);

	// currentTime updates ~4x/sec from VideoPlayer; sibling components
	// (VideoPlayer, DetectionChart) are memo'd so only KeyMetrics re-renders.
	const [currentTime, setCurrentTime] = useState(0);

	const videoPlayerRef = useRef(null);

	useEffect(() => {
		if (!videoUrl) return;
		let cancelled = false;
		fetchUrl(`filelink?key=${videoUrl}`)
			.then(data => {
				if (!cancelled && data?.url) setSignedVideoUrl(data.url);
			})
			.catch(err => console.warn('Failed to fetch video URL:', err));
		return () => {
			cancelled = true;
		};
	}, [videoUrl]);

	const handleChartPointClick = useCallback(point => {
		videoPlayerRef.current?.seekTo(point.timestamp_sec);
	}, []);

	const handleTimeUpdate = useCallback(time => setCurrentTime(time), []);

	const stats = analysis?.statistics;
	const fps = analysis?.actual_fps;
	const countingMethod = stats?.counting_method_used;
	const methodConfig = useMemo(() => getCountingMethodConfig(countingMethod), [countingMethod]);

	// Build chart data from time_series file
	useEffect(() => {
		const timeSeriesPath = analysis?.result_paths?.time_series;
		if (!timeSeriesPath) return;
		let cancelled = false;
		fetchUrl(`filelink?key=${timeSeriesPath}`).then(data => {
			if (cancelled || !data?.url) return;
			fetch(data.url)
				.then(res => res.json())
				.then(json => {
					if (!cancelled) setChartData(json);
				})
				.catch(err => console.error('Failed to fetch time series data:', err));
		});
		return () => {
			cancelled = true;
		};
	}, [analysis?.result_paths?.time_series]);

	const peakPoint = useMemo(() => {
		if (!Array.isArray(chartData) || chartData.length === 0) return null;
		const { frameField } = methodConfig;
		return chartData.reduce(
			(max, d) => (d[frameField] > max[frameField] ? d : max),
			chartData[0]
		);
	}, [chartData, methodConfig]);

	// Derive analysis period from analysis timestamps or video duration
	const startTime =
		analysis?.analysis_start_seconds != null
			? formatTimestamp(analysis.analysis_start_seconds, fps)
			: '00:00';
	const endTime =
		analysis?.analysis_end_seconds != null
			? formatTimestamp(analysis.analysis_end_seconds, fps)
			: analysis?.frames_processed && fps
				? formatTimestamp(analysis.frames_processed / fps, fps)
				: '—:—';

	const renderContent = useCallback(() => {
		// Results state
		if (jobStatus === 'finished' && results) {
			return (
				<ResultsBody>
					<VideoArea>
						<VideoPlayer
							ref={videoPlayerRef}
							videoUrl={signedVideoUrl}
							detections={detections}
							onTimeUpdate={handleTimeUpdate}
							virtualLine={analysis?.virtual_line}
						/>
						<KeyMetrics
							totalCount={stats?.total_count}
							uniqueCount={stats?.unique_count}
							average={stats?.average_count_per_frame}
							peakTime={formatTimestamp(stats?.peak_timestamp, fps)}
							maxSimultaneousCount={stats?.max_simultaneous_count}
							countingMethod={countingMethod}
							chartData={chartData}
							currentTime={currentTime}
						/>
					</VideoArea>
					{/*<AnalysisPeriod*/}
					{/*	startTime={startTime}*/}
					{/*	endTime={endTime}*/}
					{/*	onSetPeriod={onSetPeriod}*/}
					{/*/>*/}
					<DetectionChart
						data={chartData}
						peakPoint={peakPoint}
						onExportCsv={onExportCsv}
						onPointClick={handleChartPointClick}
						countingMethod={countingMethod}
					/>
				</ResultsBody>
			);
		}

		// Running or Pending state
		if (jobStatus === 'running' || jobStatus === 'pending') {
			const pct = jobProgress ?? 0;
			return (
				<StateContainer>
					<StateIcon className="spinning">
						<LuLoader size={48} />
					</StateIcon>
					<StateText>
						Job is running... Detection results will appear here when complete.
					</StateText>
					<ProgressRow>
						<ProgressTrack>
							<ProgressFill $pct={pct} />
						</ProgressTrack>
						<ProgressLabel>{pct}%</ProgressLabel>
					</ProgressRow>
				</StateContainer>
			);
		}

		// Empty state (no job)
		return (
			<StateContainer>
				<StateIcon>
					<TbVideo size={48} />
				</StateIcon>
				<StateText>No active detection job is found.</StateText>
			</StateContainer>
		);
	}, [
		jobStatus,
		jobProgress,
		signedVideoUrl,
		detections,
		results,
		stats,
		fps,
		chartData,
		peakPoint,
		currentTime,
		countingMethod,
		handleTimeUpdate,
		handleChartPointClick,
		startTime,
		endTime,
		onExportCsv,
		analysis,
	]);

	return (
		<Main>
			<MainHeader>
				<HeaderTitle>Detection Results</HeaderTitle>
				{analysis?.result_paths?.report_pdf && (
					<HeaderActions>
						<ActionButton onClick={onExport}>
							<LuFileDown size={13} /> Download Report
						</ActionButton>
					</HeaderActions>
				)}
			</MainHeader>
			{renderContent()}
		</Main>
	);
};

export default ResultsPanel;
